import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from 'src/app/app-core/services/user.service';
import Swal from 'sweetalert2';

@Component({
    selector: 'app-definir-senha',
    templateUrl: './definir-senha.component.html',
    providers: [UserService],
})
export class DefinirSenhaComponent implements OnInit, OnDestroy {
    public token: string = '';
    public etapa: 'codigo' | 'senha' = 'codigo';

    // Etapa 1 — verificação por código
    public emailMascarado: string = '';
    public digitos: string[] = ['', '', '', '', '', ''];
    public loadingEnviar: boolean = false;
    public loadingValidar: boolean = false;
    public erroValidacao: string = '';
    public countdown: number = 0;
    private countdownTimer: any = null;

    // Etapa 2 — definição de senha
    public novaSenha: string = '';
    public confirmarSenha: string = '';
    public loading: boolean = false;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private userService: UserService,
    ) {}

    ngOnInit(): void {
        this.token = this.route.snapshot.paramMap.get('token') || '';
        this.enviarCodigo();
    }

    ngOnDestroy(): void {
        if (this.countdownTimer) clearInterval(this.countdownTimer);
    }

    get codigoCompleto(): string {
        return this.digitos.join('');
    }

    trackByIndex(index: number): number {
        return index;
    }

    get podValidar(): boolean {
        return this.codigoCompleto.length === 6 && !this.loadingValidar;
    }

    enviarCodigo(): void {
        this.loadingEnviar = true;
        this.erroValidacao = '';

        this.userService.enviarCodigoConfirmacao(this.token).subscribe({
            next: (res) => {
                this.emailMascarado = res.emailMascarado;
                this.loadingEnviar = false;
                this.iniciarCountdown(120);
            },
            error: (err) => {
                this.loadingEnviar = false;
                const msg = err?.error?.message || 'Erro ao enviar código.';
                Swal.fire('Erro', msg, 'error');
            },
        });
    }

    iniciarCountdown(segundos: number): void {
        if (this.countdownTimer) clearInterval(this.countdownTimer);
        this.countdown = segundos;
        this.countdownTimer = setInterval(() => {
            this.countdown--;
            if (this.countdown <= 0) {
                clearInterval(this.countdownTimer);
                this.countdownTimer = null;
            }
        }, 1000);
    }

    get countdownFormatado(): string {
        const m = Math.floor(this.countdown / 60).toString().padStart(2, '0');
        const s = (this.countdown % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    }

    onDigitoInput(event: Event, index: number): void {
        const input = event.target as HTMLInputElement;
        const digitosDigitados = input.value.replace(/\D/g, '');
        this.erroValidacao = '';

        if (!digitosDigitados) {
            this.digitos[index] = '';
            return;
        }

        // Teclados numéricos mobile podem entregar mais de um caractere num único
        // evento de input (composição do IME). Distribuir pelas próximas caixas,
        // em vez de só manter o último dígito, evita que a caixa atual acabe com
        // 2 dígitos. Também não reescrevemos input.value aqui: fazer isso durante
        // a composição é o que faz o Android duplicar o próximo dígito — o binding
        // [value]="d" já corrige a caixa no próximo ciclo do Angular.
        let cursor = index;
        for (const digito of digitosDigitados) {
            if (cursor > 5) break;
            this.digitos[cursor] = digito;
            cursor++;
        }

        // O pulo de foco precisa ser síncrono: adiá-lo com setTimeout abre uma
        // janela onde o auto-repeat do teclado físico entrega a mesma tecla de
        // novo antes do foco mudar, e ela acaba preenchendo a caixa seguinte também.
        const proximoIndex = Math.min(cursor, 5);
        if (proximoIndex !== index) {
            const proximo = document.getElementById(`digito-${proximoIndex}`) as HTMLInputElement;
            proximo?.focus();
        }
    }

    onDigitoKeydown(event: KeyboardEvent, index: number): void {
        if (event.key === 'Backspace' && !this.digitos[index] && index > 0) {
            const prev = document.getElementById(`digito-${index - 1}`) as HTMLInputElement;
            prev?.focus();
        }
    }

    onDigitoPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const texto = event.clipboardData?.getData('text').replace(/\D/g, '').slice(0, 6) || '';
        texto.split('').forEach((char, i) => { this.digitos[i] = char; });
        const nextEmpty = this.digitos.findIndex(d => !d);
        const focusIndex = nextEmpty === -1 ? 5 : nextEmpty;
        const el = document.getElementById(`digito-${focusIndex}`) as HTMLInputElement;
        el?.focus();
    }

    validarCodigo(): void {
        if (!this.podValidar) return;
        this.loadingValidar = true;
        this.erroValidacao = '';

        this.userService.validarCodigoConfirmacao(this.token, this.codigoCompleto).subscribe({
            next: () => {
                this.loadingValidar = false;
                this.etapa = 'senha';
            },
            error: (err) => {
                this.loadingValidar = false;
                this.erroValidacao = err?.error?.message || 'Código inválido. Verifique e tente novamente.';
            },
        });
    }

    salvar(): void {
        if (!this.novaSenha || this.novaSenha.length < 6) {
            Swal.fire('Atenção', 'A senha deve ter no mínimo 6 caracteres.', 'warning');
            return;
        }

        if (this.novaSenha !== this.confirmarSenha) {
            Swal.fire('Atenção', 'As senhas não coincidem.', 'warning');
            return;
        }

        this.loading = true;

        this.userService.definirSenha(this.token, this.novaSenha).subscribe({
            next: () => {
                this.loading = false;
                Swal.fire('Sucesso!', 'Sua senha foi salva com sucesso.', 'success').then(() => {
                    this.router.navigate(['/principal']);
                });
            },
            error: (err: any) => {
                this.loading = false;
                const msg = err?.error?.message || 'Erro ao salvar senha.';
                Swal.fire('Erro', msg, 'error');
            },
        });
    }
}
